import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetCartId } from '@app/context/CartIdProvider/CartIdProvider';

import { useAddCouponToCart } from './useAddCouponToCart';
import useAddProductsToCart from './useAddProductsToCart';

const QUANTITY_SEARCH_KEY = 'qty';
const SKU_SEARCH_KEY = 'product';
const COUPON_KEY = 'coupon';

export const useAddProductFromUrlQuery = () => {
  const { search } = useLocation();
  const { cartId } = useGetCartId();
  const { addItems, addProductsLoading, addProductsResult } =
    useAddProductsToCart();
  const {
    applyCoupon,
    loading: applyCouponLoading,
    error: applyCouponError,
    data: applyCouponData,
  } = useAddCouponToCart();

  const query = useMemo(() => new URLSearchParams(search), [search]);

  const quantity = Number(query.get(QUANTITY_SEARCH_KEY) ?? 0);
  const sku = query.get(SKU_SEARCH_KEY) ?? '';
  const coupon = query.get(COUPON_KEY) ?? '';

  const result = addProductsResult?.data?.magento2?.addProductsToCart;
  const hasResult = Boolean(result?.cart.id);
  const hasError = Boolean(result?.userErrors?.length);
  const couponWasApplied = Boolean(
    applyCouponData?.magento2?.applyCouponToCart?.cart.appliedCoupons?.find(
      (appliedCoupon) => appliedCoupon?.code === coupon,
    ),
  );

  const shouldAddProduct = sku.length > 0 && quantity > 0;
  const shouldAddCoupon = coupon.length > 0;

  useEffect(() => {
    const url = new URL(window.location.href);
    const query = new URLSearchParams(url.search);

    if (hasResult && !hasError) {
      query.delete(QUANTITY_SEARCH_KEY);
      query.delete(SKU_SEARCH_KEY);
    }

    if (couponWasApplied && !applyCouponError) {
      query.delete(COUPON_KEY);
    }

    // Replace the URL only if necessary
    const newSearch = query.toString();
    const urlWithoutQueries =
      url.origin + url.pathname + (newSearch ? `?${newSearch}` : '');
    if (window.location.href !== urlWithoutQueries) {
      window.history.replaceState(null, '', urlWithoutQueries);
    }
  }, [hasResult, hasError, couponWasApplied, applyCouponError]);

  useEffect(() => {
    if (!cartId) return;

    if (shouldAddProduct) {
      const addVars = { sku, quantity };
      addItems([addVars]);
    }

    if (shouldAddCoupon) {
      applyCoupon({
        variables: { cartId, couponCode: coupon },
      });
    }
  }, [
    cartId,
    sku,
    quantity,
    shouldAddProduct,
    shouldAddCoupon,
    addItems,
    applyCoupon,
    coupon,
  ]);

  return {
    loading: !shouldAddProduct
      ? false
      : addProductsLoading || applyCouponLoading,
  };
};
